import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEdit, FaUser, FaTrophy, FaUsers, FaChess, FaPlus, FaTrash, FaClipboard } from 'react-icons/fa';
import { getTrainerProfile, updateTrainerProfile, uploadProfilePicture, removeProfilePicture } from '../api/trainerProfileApi';
import { Button, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '../utils/apiError';

const TOAST_OPTIONS = {
    position: "top-right",
    autoClose: 3000,
    theme: "colored",
};

const TrainerProfile = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editSection, setEditSection] = useState(null); // 'basic', 'rewards', 'achievements'
    
    // Edit states
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [newReward, setNewReward] = useState({ title: '', description: '' });
    const [editingRewardIndex, setEditingRewardIndex] = useState(null);
    const [newAchievement, setNewAchievement] = useState({ title: '', description: '' });
    const [editingAchievementIndex, setEditingAchievementIndex] = useState(null);
    const [rewards, setRewards] = useState([]);
    const [achievements, setAchievements] = useState([]);

    useEffect(() => {
        fetchProfile();
    }, []);

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await getTrainerProfile();
            if (response.status && response.data) {
                setProfile(response.data);
                setEditName(response.data.trainer.name);
                setEditPhone(response.data.trainer.phoneNumber);
                setRewards(response.data.trainer.rewards || []);
                setAchievements(response.data.trainer.achievements || []);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            toast.error(getApiErrorMessage(error, 'Failed to load profile'), TOAST_OPTIONS);
        } finally {
            setLoading(false);
        }
    };

    const handleProfilePictureChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file', TOAST_OPTIONS);
                return;
            }

            // Check file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('File size should be less than 5MB', TOAST_OPTIONS);
                return;
            }

            try {
                const response = await uploadProfilePicture(file);
                if (response?.status === false || response?.success === false) {
                    toast.error(response?.msg || response?.message || 'Failed to upload profile picture', TOAST_OPTIONS);
                    return;
                }
                toast.success(response?.msg || response?.message || 'Profile picture uploaded successfully!', TOAST_OPTIONS);
                fetchProfile();
            } catch (error) {
                console.error('Error uploading profile picture:', error);
                toast.error(getApiErrorMessage(error, 'Failed to upload profile picture'), TOAST_OPTIONS);
            }
        }
    };

    const handleRemoveProfilePicture = async () => {
        try {
            const response = await removeProfilePicture();
            if (response?.status === false || response?.success === false) {
                toast.error(response?.msg || response?.message || 'Failed to remove profile picture', TOAST_OPTIONS);
                return;
            }
            toast.success(response?.msg || response?.message || 'Profile picture removed successfully!', TOAST_OPTIONS);
            fetchProfile();
        } catch (error) {
            console.error('Error removing profile picture:', error);
            toast.error(getApiErrorMessage(error, 'Failed to remove profile picture'), TOAST_OPTIONS);
        }
    };

    const handleCopyReferralCode = () => {
        if (!profile?.trainer?.AdminReferralCode) return;
        navigator.clipboard.writeText(profile.trainer.AdminReferralCode).then(() => {
            toast.success('Referral code copied to clipboard!', TOAST_OPTIONS);
        }).catch((err) => {
            console.error('Failed to copy referral code: ', err);
            toast.error('Failed to copy referral code', TOAST_OPTIONS);
        });
    };

    const handleBasicInfoSave = async () => {
        try {
            const response = await updateTrainerProfile({
                name: editName,
                phoneNumber: editPhone
            });
            if (response?.status === false || response?.success === false) {
                toast.error(response?.msg || response?.message || 'Failed to update profile', TOAST_OPTIONS);
                return;
            }
            toast.success(response?.msg || response?.message || 'Profile updated successfully!', TOAST_OPTIONS);
            setEditing(false);
            setEditSection(null);
            fetchProfile();
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error(getApiErrorMessage(error, 'Failed to update profile'), TOAST_OPTIONS);
        }
    };

    const handleAddReward = () => {
        const title = newReward.title.trim();
        const description = newReward.description.trim();

        if (!title || !description) {
            toast.error('Please fill in both title and description', TOAST_OPTIONS);
            return;
        }
        if (title.length > 100) {
            toast.error('Reward title cannot exceed 100 characters', TOAST_OPTIONS);
            return;
        }
        if (description.length > 500) {
            toast.error('Reward description cannot exceed 500 characters', TOAST_OPTIONS);
            return;
        }

        if (editingRewardIndex !== null) {
            const updated = [...rewards];
            updated[editingRewardIndex] = { title, description };
            setRewards(updated);
            setEditingRewardIndex(null);
            setNewReward({ title: '', description: '' });
            toast.success('Reward updated in list. Click Save Rewards to persist changes.', TOAST_OPTIONS);
        } else {
            setRewards([...rewards, { title, description }]);
            setNewReward({ title: '', description: '' });
        }
    };

    const handleStartEditReward = (index) => {
        setEditingRewardIndex(index);
        setNewReward({
            title: rewards[index].title || '',
            description: rewards[index].description || '',
        });
    };

    const handleCancelEditReward = () => {
        setEditingRewardIndex(null);
        setNewReward({ title: '', description: '' });
    };

    const handleRemoveReward = (index) => {
        if (editingRewardIndex === index) {
            handleCancelEditReward();
        } else if (editingRewardIndex !== null && editingRewardIndex > index) {
            setEditingRewardIndex(editingRewardIndex - 1);
        }
        setRewards(rewards.filter((_, i) => i !== index));
    };

    const handleSaveRewards = async () => {
        try {
            const response = await updateTrainerProfile({ rewards });
            if (response?.status === false || response?.success === false) {
                toast.error(response?.msg || response?.message || 'Failed to update rewards', TOAST_OPTIONS);
                return;
            }
            toast.success(response?.msg || response?.message || 'Rewards updated successfully!', TOAST_OPTIONS);
            setEditing(false);
            setEditSection(null);
            setEditingRewardIndex(null);
            setNewReward({ title: '', description: '' });
            fetchProfile();
        } catch (error) {
            console.error('Error updating rewards:', error);
            toast.error(getApiErrorMessage(error, 'Failed to update rewards'), TOAST_OPTIONS);
        }
    };

    const handleAddAchievement = () => {
        const title = newAchievement.title.trim();
        const description = newAchievement.description.trim();

        if (!title || !description) {
            toast.error('Please fill in both title and description', TOAST_OPTIONS);
            return;
        }
        if (title.length > 100) {
            toast.error('Achievement title cannot exceed 100 characters', TOAST_OPTIONS);
            return;
        }
        if (description.length > 500) {
            toast.error('Achievement description cannot exceed 500 characters', TOAST_OPTIONS);
            return;
        }

        if (editingAchievementIndex !== null) {
            const updated = [...achievements];
            updated[editingAchievementIndex] = { title, description };
            setAchievements(updated);
            setEditingAchievementIndex(null);
            setNewAchievement({ title: '', description: '' });
            toast.success('Achievement updated in list. Click Save Achievements to persist changes.', TOAST_OPTIONS);
        } else {
            setAchievements([...achievements, { title, description }]);
            setNewAchievement({ title: '', description: '' });
        }
    };

    const handleStartEditAchievement = (index) => {
        setEditingAchievementIndex(index);
        setNewAchievement({
            title: achievements[index].title || '',
            description: achievements[index].description || '',
        });
    };

    const handleCancelEditAchievement = () => {
        setEditingAchievementIndex(null);
        setNewAchievement({ title: '', description: '' });
    };

    const handleRemoveAchievement = (index) => {
        if (editingAchievementIndex === index) {
            handleCancelEditAchievement();
        } else if (editingAchievementIndex !== null && editingAchievementIndex > index) {
            setEditingAchievementIndex(editingAchievementIndex - 1);
        }
        setAchievements(achievements.filter((_, i) => i !== index));
    };

    const handleSaveAchievements = async () => {
        try {
            const response = await updateTrainerProfile({ achievements });
            if (response?.status === false || response?.success === false) {
                toast.error(response?.msg || response?.message || 'Failed to update achievements', TOAST_OPTIONS);
                return;
            }
            toast.success(response?.msg || response?.message || 'Achievements updated successfully!', TOAST_OPTIONS);
            setEditing(false);
            setEditSection(null);
            setEditingAchievementIndex(null);
            setNewAchievement({ title: '', description: '' });
            fetchProfile();
        } catch (error) {
            console.error('Error updating achievements:', error);
            toast.error(getApiErrorMessage(error, 'Failed to update achievements'), TOAST_OPTIONS);
        }
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingText}>Loading profile...</div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div style={styles.container}>
                <div style={styles.errorText}>Failed to load profile</div>
            </div>
        );
    }

    const { trainer, tournaments, users, stats } = profile;

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleBackClick}
                    sx={{ marginBottom: 2 }}
                >
                    Back to Dashboard
                </Button>
                <h1 style={styles.title}>My Profile</h1>
            </div>

            {/* Profile Card */}
            <div style={styles.profileCard}>
                {/* Profile Picture Section */}
                <div style={styles.profilePictureSection}>
                    <div style={styles.profilePictureContainer}>
                        {trainer.profilePicture ? (
                            <img 
                                src={trainer.profilePicture} 
                                alt="Profile" 
                                style={styles.profilePicture}
                            />
                        ) : (
                            <div style={styles.defaultProfilePicture}>
                                <FaUser size={60} color="#fff" />
                            </div>
                        )}
                        <label htmlFor="profile-picture-upload" style={styles.editPictureButton} title="Upload Profile Photo">
                            <FaEdit size={16} />
                            <input
                                id="profile-picture-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleProfilePictureChange}
                                style={styles.fileInput}
                            />
                        </label>
                        {trainer.profilePicture && (
                            <button
                                type="button"
                                onClick={handleRemoveProfilePicture}
                                style={styles.removePictureButton}
                                title="Remove Profile Photo"
                            >
                                <FaTrash size={14} />
                            </button>
                        )}
                    </div>
                    {/* {trainer.profilePicture && (
                        <div style={{ marginTop: '8px' }}>
                            <Button
                                variant="text"
                                color="error"
                                size="small"
                                startIcon={<FaTrash size={12} />}
                                onClick={handleRemoveProfilePicture}
                                sx={{ textTransform: 'none', fontSize: '13px' }}
                            >
                                Remove Profile Photo
                            </Button>
                        </div>
                    )} */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title={trainer.name || ''} arrow>
                            <h2 style={styles.profileName}>{trainer.name}</h2>
                        </Tooltip>
                    </div>
                    <p style={styles.profileRole}>Trainer</p>
                    <div style={styles.referralCodeContainer}>
                        <span style={styles.referralCode}>Referral Code: <strong>{trainer.AdminReferralCode || 'N/A'}</strong></span>
                        {trainer.AdminReferralCode && (
                            <Tooltip title="Copy Referral Code" arrow>
                                <button
                                    onClick={handleCopyReferralCode}
                                    style={styles.copyButton}
                                    type="button"
                                    aria-label="Copy Referral Code"
                                >
                                    <FaClipboard size={14} />
                                </button>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {/* Stats Section */}
                <div style={styles.statsContainer}>
                    <div style={styles.statCard}>
                        <FaChess size={30} color="#4CAF50" />
                        <div style={styles.statNumber}>{stats.totalTournaments}</div>
                        <div style={styles.statLabel}>Tournaments</div>
                    </div>
                    <div style={styles.statCard}>
                        <FaUsers size={30} color="#2196F3" />
                        <div style={styles.statNumber}>{stats.totalUsers}</div>
                        <div style={styles.statLabel}>Members</div>
                    </div>
                </div>
            </div>

            {/* Basic Information Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Basic Information</h3>
                </div>
                <div style={styles.sectionContent}>
                    {editSection === 'basic' ? (
                        <div style={styles.editForm}>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Name</label>
                                <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Email</label>
                                <input
                                    type="email"
                                    value={trainer.email}
                                    disabled
                                    style={{...styles.input, backgroundColor: '#f0f0f0', cursor: 'not-allowed'}}
                                />
                                <small style={styles.helpText}>Email cannot be changed</small>
                            </div>
                            <div style={styles.formGroup}>
                                <label style={styles.label}>Phone Number</label>
                                <input
                                    type="text"
                                    value={editPhone}
                                    onChange={(e) => setEditPhone(e.target.value)}
                                    style={styles.input}
                                />
                            </div>
                            <div style={styles.buttonGroup}>
                                <button style={styles.saveButton} onClick={handleBasicInfoSave}>
                                    Save Changes
                                </button>
                                <button
                                    style={styles.cancelButton}
                                    onClick={() => {
                                        setEditing(false);
                                        setEditSection(null);
                                        setEditName(trainer.name);
                                        setEditPhone(trainer.phoneNumber);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={styles.infoGrid}>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Name:</span>
                                <Tooltip title={trainer.name || ''} arrow>
                                    <span style={styles.infoValue}>{trainer.name || 'N/A'}</span>
                                </Tooltip>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Email:</span>
                                <Tooltip title={trainer.email || ''} arrow>
                                    <span style={styles.infoValue}>{trainer.email || 'N/A'}</span>
                                </Tooltip>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Phone:</span>
                                <Tooltip title={trainer.phoneNumber || ''} arrow>
                                    <span style={styles.infoValue}>{trainer.phoneNumber || 'N/A'}</span>
                                </Tooltip>
                            </div>
                            <div style={styles.infoItem}>
                                <span style={styles.infoLabel}>Member Since:</span>
                                <span style={styles.infoValue}>
                                    {trainer.createdAt ? new Date(trainer.createdAt).toLocaleDateString() : 'N/A'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>


            {/* Rewards Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>
                        <FaTrophy color="#FFD700" /> Rewards
                    </h3>
                    {editSection !== 'rewards' && (
                        <button
                            style={styles.editButton}
                            onClick={() => {
                                setEditing(true);
                                setEditSection('rewards');
                                setEditingRewardIndex(null);
                                setNewReward({ title: '', description: '' });
                            }}
                        >
                            <FaEdit /> Edit
                        </button>
                    )}
                </div>
                <div style={styles.sectionContent}>
                    {editSection === 'rewards' ? (
                        <div style={styles.editForm}>
                            <div style={styles.addItemForm}>
                                <div>
                                    <input
                                        type="text"
                                        value={newReward.title}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.length > 100) return;
                                            setNewReward({ ...newReward, title: val });
                                        }}
                                        placeholder="Reward title (e.g., Tournament Winner)"
                                        maxLength={100}
                                        style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                                        <span style={{ fontSize: '12px', color: (newReward.title?.length || 0) >= 100 ? '#d32f2f' : '#888' }}>
                                            {newReward.title?.length || 0}/100
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <textarea
                                        value={newReward.description}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.length > 500) return;
                                            setNewReward({ ...newReward, description: val });
                                        }}
                                        placeholder="Reward description (e.g., Won 1st place in State Championship)"
                                        maxLength={500}
                                        style={{ ...styles.textarea, width: '100%', boxSizing: 'border-box' }}
                                        rows="3"
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                                        <span style={{ fontSize: '12px', color: (newReward.description?.length || 0) >= 500 ? '#d32f2f' : '#888' }}>
                                            {newReward.description?.length || 0}/500
                                        </span>
                                    </div>
                                </div>
                                {editingRewardIndex !== null ? (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button style={styles.addButton} onClick={handleAddReward}>
                                            <FaEdit /> Update Reward
                                        </button>
                                        <button style={styles.cancelEditButton} onClick={handleCancelEditReward}>
                                            Cancel Edit
                                        </button>
                                    </div>
                                ) : (
                                    <button style={styles.addButton} onClick={handleAddReward}>
                                        <FaPlus /> Add Reward
                                    </button>
                                )}
                            </div>
                            <div style={styles.cardsGrid}>
                                {rewards.length === 0 ? (
                                    <p style={styles.emptyText}>No rewards added yet</p>
                                ) : (
                                    rewards.map((reward, index) => (
                                        <div key={index} style={styles.editCard}>
                                            <div style={styles.editCardHeader}>
                                                <FaTrophy color="#FFD700" size={20} style={{ flexShrink: 0 }} />
                                                <Tooltip title={reward.title || ''} arrow placement="top">
                                                    <h4 style={styles.editCardTitle}>{reward.title}</h4>
                                                </Tooltip>
                                            </div>
                                            <Tooltip title={reward.description || ''} arrow placement="top">
                                                <p style={styles.editCardDescription}>{reward.description}</p>
                                            </Tooltip>
                                            <div style={styles.cardActions}>
                                                <button
                                                    style={styles.editCardButton}
                                                    onClick={() => handleStartEditReward(index)}
                                                >
                                                    <FaEdit /> Edit
                                                </button>
                                                <button
                                                    style={styles.deleteCardButton}
                                                    onClick={() => handleRemoveReward(index)}
                                                >
                                                    <FaTrash /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div style={styles.buttonGroup}>
                                <button
                                    style={styles.cancelButton}
                                    onClick={() => {
                                        setEditing(false);
                                        setEditSection(null);
                                        setEditingRewardIndex(null);
                                        setRewards(trainer.rewards || []);
                                        setNewReward({ title: '', description: '' });
                                    }}
                                >
                                    Cancel
                                </button>
                                <button style={styles.saveButton} onClick={handleSaveRewards}>
                                    Save Rewards
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={styles.cardsGrid}>
                            {(!rewards || rewards.length === 0) ? (
                                <p style={styles.emptyText}>No rewards added yet. Click Edit to add rewards!</p>
                            ) : (
                                rewards.map((reward, index) => (
                                    <div key={index} style={styles.rewardCard}>
                                        <div style={styles.cardIcon}>
                                            <FaTrophy color="#FFD700" size={24} />
                                        </div>
                                        <div style={styles.cardContent}>
                                            <Tooltip title={reward.title || ''} arrow placement="top">
                                                <h4 style={styles.cardTitle}>{reward.title}</h4>
                                            </Tooltip>
                                            <Tooltip title={reward.description || ''} arrow placement="top">
                                                <p style={styles.cardDescription}>{reward.description}</p>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Achievements Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>
                        <FaTrophy color="#4CAF50" /> Achievements
                    </h3>
                    {editSection !== 'achievements' && (
                        <button
                            style={styles.editButton}
                            onClick={() => {
                                setEditing(true);
                                setEditSection('achievements');
                                setEditingAchievementIndex(null);
                                setNewAchievement({ title: '', description: '' });
                            }}
                        >
                            <FaEdit /> Edit
                        </button>
                    )}
                </div>
                <div style={styles.sectionContent}>
                    {editSection === 'achievements' ? (
                        <div style={styles.editForm}>
                            <div style={styles.addItemForm}>
                                <div>
                                    <input
                                        type="text"
                                        value={newAchievement.title}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.length > 100) return;
                                            setNewAchievement({ ...newAchievement, title: val });
                                        }}
                                        placeholder="Achievement title (e.g., FIDE Master)"
                                        maxLength={100}
                                        style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                                        <span style={{ fontSize: '12px', color: (newAchievement.title?.length || 0) >= 100 ? '#d32f2f' : '#888' }}>
                                            {newAchievement.title?.length || 0}/100
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <textarea
                                        value={newAchievement.description}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val.length > 500) return;
                                            setNewAchievement({ ...newAchievement, description: val });
                                        }}
                                        placeholder="Achievement description (e.g., Achieved FIDE Master title in 2023)"
                                        maxLength={500}
                                        style={{ ...styles.textarea, width: '100%', boxSizing: 'border-box' }}
                                        rows="3"
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                                        <span style={{ fontSize: '12px', color: (newAchievement.description?.length || 0) >= 500 ? '#d32f2f' : '#888' }}>
                                            {newAchievement.description?.length || 0}/500
                                        </span>
                                    </div>
                                </div>
                                {editingAchievementIndex !== null ? (
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button style={styles.addButton} onClick={handleAddAchievement}>
                                            <FaEdit /> Update Achievement
                                        </button>
                                        <button style={styles.cancelEditButton} onClick={handleCancelEditAchievement}>
                                            Cancel Edit
                                        </button>
                                    </div>
                                ) : (
                                    <button style={styles.addButton} onClick={handleAddAchievement}>
                                        <FaPlus /> Add Achievement
                                    </button>
                                )}
                            </div>
                            <div style={styles.cardsGrid}>
                                {achievements.length === 0 ? (
                                    <p style={styles.emptyText}>No achievements added yet</p>
                                ) : (
                                    achievements.map((achievement, index) => (
                                        <div key={index} style={styles.editCard}>
                                            <div style={styles.editCardHeader}>
                                                <FaTrophy color="#4CAF50" size={20} style={{ flexShrink: 0 }} />
                                                <Tooltip title={achievement.title || ''} arrow placement="top">
                                                    <h4 style={styles.editCardTitle}>{achievement.title}</h4>
                                                </Tooltip>
                                            </div>
                                            <Tooltip title={achievement.description || ''} arrow placement="top">
                                                <p style={styles.editCardDescription}>{achievement.description}</p>
                                            </Tooltip>
                                            <div style={styles.cardActions}>
                                                <button
                                                    style={styles.editCardButton}
                                                    onClick={() => handleStartEditAchievement(index)}
                                                >
                                                    <FaEdit /> Edit
                                                </button>
                                                <button
                                                    style={styles.deleteCardButton}
                                                    onClick={() => handleRemoveAchievement(index)}
                                                >
                                                    <FaTrash /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div style={styles.buttonGroup}>
                                <button
                                    style={styles.cancelButton}
                                    onClick={() => {
                                        setEditing(false);
                                        setEditSection(null);
                                        setEditingAchievementIndex(null);
                                        setAchievements(trainer.achievements || []);
                                        setNewAchievement({ title: '', description: '' });
                                    }}
                                >
                                    Cancel
                                </button>
                                <button style={styles.saveButton} onClick={handleSaveAchievements}>
                                    Save Achievements
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div style={styles.cardsGrid}>
                            {(!achievements || achievements.length === 0) ? (
                                <p style={styles.emptyText}>No achievements added yet. Click Edit to add achievements!</p>
                            ) : (
                                achievements.map((achievement, index) => (
                                    <div key={index} style={styles.achievementCard}>
                                        <div style={styles.cardIcon}>
                                            <FaTrophy color="#4CAF50" size={24} />
                                        </div>
                                        <div style={styles.cardContent}>
                                            <Tooltip title={achievement.title || ''} arrow placement="top">
                                                <h4 style={styles.cardTitle}>{achievement.title}</h4>
                                            </Tooltip>
                                            <Tooltip title={achievement.description || ''} arrow placement="top">
                                                <p style={styles.cardDescription}>{achievement.description}</p>
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Tournaments Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Recent Tournaments</h3>
                    <button
                        style={styles.viewAllButton}
                        onClick={() => navigate('/fetch-tournaments')}
                    >
                        View All
                    </button>
                </div>
                <div style={styles.sectionContent}>
                    {tournaments.length === 0 ? (
                        <p style={styles.emptyText}>No tournaments created yet</p>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.tableHeader}>Code</th>
                                        <th style={styles.tableHeader}>Format</th>
                                        <th style={styles.tableHeader}>Status</th>
                                        <th style={styles.tableHeader}>Participants</th>
                                        <th style={styles.tableHeader}>Start Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tournaments.slice(0, 5).map((tournament, index) => (
                                        <tr key={index} style={styles.tableRow}>
                                            <td style={styles.tableCell}>{tournament.unique_code}</td>
                                            <td style={styles.tableCell}>{tournament.format}</td>
                                            <td style={styles.tableCell}>
                                                <span style={{
                                                    ...styles.statusBadge,
                                                    backgroundColor: 
                                                        tournament.state === 'Completed' ? '#4CAF50' :
                                                        tournament.state === 'In-Progress' ? '#FF9800' :
                                                        tournament.state === 'Cancelled' ? '#F44336' : '#2196F3'
                                                }}>
                                                    {tournament.state}
                                                </span>
                                            </td>
                                            <td style={styles.tableCell}>
                                                {tournament.participants_count}/{tournament.max_participants}
                                            </td>
                                            <td style={styles.tableCell}>
                                                {new Date(tournament.start_time).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Users Section */}
            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h3 style={styles.sectionTitle}>Recent Members</h3>
                    <button
                        style={styles.viewAllButton}
                        onClick={() => navigate('/team-manage')}
                    >
                        View All
                    </button>
                </div>
                <div style={styles.sectionContent}>
                    {users.length === 0 ? (
                        <p style={styles.emptyText}>No users registered yet</p>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.tableHeader}>Name</th>
                                        <th style={styles.tableHeader}>Mobile</th>
                                        {/* <th style={styles.tableHeader}>Team</th> */}
                                        <th style={styles.tableHeader}>Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.slice(0, 5).map((userTeam, index) => (
                                        <tr key={index} style={styles.tableRow}>
                                            <td style={styles.tableCell}>{userTeam.users?.username || 'N/A'}</td>
                                            <td style={styles.tableCell}>{userTeam.users?.mobile || 'N/A'}</td>
                                            {/* <td style={styles.tableCell}>{userTeam.team?.name || 'N/A'}</td> */}
                                            <td style={styles.tableCell}>{userTeam.users?.points || 0}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        fontFamily: 'Arial, sans-serif',
    },
    header: {
        marginBottom: '30px',
    },
    backButton: {
        padding: '10px 20px',
        backgroundColor: '#f0f0f0',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        marginBottom: '10px',
    },
    title: {
        fontSize: '32px',
        color: '#333',
        margin: '0',
    },
    profileCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        padding: '30px',
        marginBottom: '20px',
    },
    profilePictureSection: {
        textAlign: 'center',
        marginBottom: '20px',
    },
    profilePictureContainer: {
        position: 'relative',
        display: 'inline-block',
    },
    profilePicture: {
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '4px solid #2196F3',
    },
    defaultProfilePicture: {
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        backgroundColor: '#2196F3',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    editPictureButton: {
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        backgroundColor: '#2196F3',
        color: '#fff',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    },
    removePictureButton: {
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        backgroundColor: '#f44336',
        color: '#fff',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: 'none',
        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
    },
    fileInput: {
        display: 'none',
    },
    profileName: {
        fontSize: '28px',
        color: '#333',
        margin: '15px 0 5px 0',
        maxWidth: '320px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        textAlign: 'center',
    },
    profileRole: {
        fontSize: '18px',
        color: '#666',
        margin: '0 0 10px 0',
    },
    referralCodeContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        flexWrap: 'wrap',
    },
    referralCode: {
        fontSize: '14px',
        color: '#888',
        margin: '0',
    },
    copyButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#2196F3',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px',
        borderRadius: '4px',
        transition: 'color 0.2s',
    },
    statsContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '30px',
        marginTop: '20px',
    },
    statCard: {
        textAlign: 'center',
        padding: '20px',
        backgroundColor: '#f9f9f9',
        borderRadius: '10px',
        minWidth: '150px',
    },
    statNumber: {
        fontSize: '36px',
        fontWeight: 'bold',
        color: '#333',
        margin: '10px 0 5px 0',
    },
    statLabel: {
        fontSize: '14px',
        color: '#666',
    },
    section: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px',
    },
    sectionHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '2px solid #f0f0f0',
        paddingBottom: '10px',
    },
    sectionTitle: {
        fontSize: '22px',
        color: '#333',
        margin: '0',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    sectionContent: {
        marginTop: '20px',
    },
    editButton: {
        padding: '8px 16px',
        backgroundColor: '#2196F3',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
    },
    viewAllButton: {
        padding: '8px 16px',
        backgroundColor: '#4CAF50',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '14px',
    },
    infoGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '15px',
    },
    infoItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        minWidth: 0,
    },
    infoLabel: {
        fontSize: '14px',
        color: '#666',
        fontWeight: 'bold',
    },
    infoValue: {
        fontSize: '16px',
        color: '#333',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        maxWidth: '100%',
    },
    editForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
    },
    label: {
        fontSize: '14px',
        color: '#666',
        fontWeight: 'bold',
    },
    input: {
        padding: '10px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '5px',
    },
    helpText: {
        fontSize: '12px',
        color: '#888',
    },
    buttonGroup: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '20px',
    },
    saveButton: {
        padding: '10px 24px',
        backgroundColor: '#4CAF50',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: '600',
        transition: 'background-color 0.2s',
    },
    cancelButton: {
        padding: '10px 24px',
        backgroundColor: '#f44336',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: '600',
        transition: 'background-color 0.2s',
    },
    cancelEditButton: {
        padding: '12px 20px',
        backgroundColor: '#6c757d',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: 'bold',
        transition: 'background-color 0.2s',
    },
    addItemContainer: {
        display: 'flex',
        gap: '10px',
    },
    addItemForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '2px dashed #ddd',
    },
    textarea: {
        padding: '12px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '5px',
        fontFamily: 'Arial, sans-serif',
        resize: 'vertical',
        minHeight: '80px',
    },
    addButton: {
        padding: '12px 24px',
        backgroundColor: '#2196F3',
        color: '#fff',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '15px',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        whiteSpace: 'nowrap',
        transition: 'background-color 0.3s ease',
        alignSelf: 'flex-start',
    },
    cardsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '16px',
        marginTop: '10px',
        maxHeight: '520px',
        overflowY: 'auto',
        padding: '4px 6px 4px 2px',
    },
    rewardCard: {
        backgroundColor: '#fffbf0',
        border: '2px solid #FFD700',
        borderRadius: '10px',
        padding: '16px',
        display: 'flex',
        gap: '12px',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'default',
        boxShadow: '0 2px 8px rgba(255, 215, 0, 0.1)',
        minHeight: '130px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    achievementCard: {
        backgroundColor: '#f1f8f4',
        border: '2px solid #4CAF50',
        borderRadius: '10px',
        padding: '16px',
        display: 'flex',
        gap: '12px',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        cursor: 'default',
        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.1)',
        minHeight: '130px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    cardIcon: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: '2px',
    },
    cardContent: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    cardTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#333',
        lineHeight: '1.3',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: '100%',
    },
    cardDescription: {
        margin: 0,
        fontSize: '14px',
        color: '#555',
        lineHeight: '1.4',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        width: '100%',
    },
    editCard: {
        backgroundColor: '#fff',
        border: '2px solid #e0e0e0',
        borderRadius: '10px',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        minHeight: '150px',
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    editCardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #f0f0f0',
        minWidth: 0,
    },
    editCardTitle: {
        margin: 0,
        fontSize: '15px',
        fontWeight: 'bold',
        color: '#333',
        flex: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    editCardDescription: {
        margin: 0,
        fontSize: '13px',
        color: '#666',
        lineHeight: '1.4',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        wordBreak: 'break-word',
        flex: 1,
    },
    cardActions: {
        display: 'flex',
        gap: '8px',
        marginTop: 'auto',
        paddingTop: '6px',
    },
    editCardButton: {
        padding: '6px 12px',
        backgroundColor: '#2196F3',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        transition: 'background-color 0.2s',
    },
    deleteCardButton: {
        padding: '6px 12px',
        backgroundColor: '#f44336',
        color: '#fff',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        transition: 'background-color 0.2s',
    },
    itemsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    listItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        backgroundColor: '#f9f9f9',
        borderRadius: '5px',
    },
    itemText: {
        fontSize: '16px',
        color: '#333',
    },
    deleteButton: {
        padding: '5px 10px',
        backgroundColor: '#f44336',
        color: '#fff',
        border: 'none',
        borderRadius: '3px',
        cursor: 'pointer',
        fontSize: '12px',
    },
    emptyText: {
        textAlign: 'center',
        color: '#888',
        fontSize: '16px',
        padding: '10px 20px',
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '120px',
    },
    tableContainer: {
        overflowX: 'auto',
        width: '100%',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'auto',
    },
    tableRow: {
        transition: 'background-color 0.2s ease',
    },
    tableHeader: {
        backgroundColor: '#f5f5f5',
        padding: '12px 16px',
        textAlign: 'left',
        borderBottom: '2px solid #ddd',
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#666',
        verticalAlign: 'middle',
        whiteSpace: 'nowrap',
    },
    tableCell: {
        padding: '12px 16px',
        borderBottom: '1px solid #eee',
        fontSize: '14px',
        color: '#333',
        verticalAlign: 'middle',
        textAlign: 'left',
    },
    statusBadge: {
        padding: '4px 12px',
        borderRadius: '12px',
        color: '#fff',
        fontSize: '12px',
        fontWeight: 'bold',
        display: 'inline-block',
        textAlign: 'center',
        minWidth: '90px',
    },
    loadingText: {
        textAlign: 'center',
        fontSize: '20px',
        color: '#666',
        marginTop: '50px',
    },
    errorText: {
        textAlign: 'center',
        fontSize: '20px',
        color: '#f44336',
        marginTop: '50px',
    },
};

export default TrainerProfile;

